/** Painting helpers shared by the maps' painted panels (murals, billboards, signs). */

type G = CanvasRenderingContext2D;

/**
 * A hero's rendered bust, drawn round in the map's one ink line (`ink`, "#rrggbb") along its
 * silhouette and every boundary between flat colours (not the soft steps of its shading), and
 * lifted until its bright tones read like paint.
 */
export function inkBust(img: HTMLImageElement, ink: string): HTMLCanvasElement {
  const S = img.naturalWidth;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const g = cv.getContext("2d", { willReadFrequently: true })!;
  g.drawImage(img, 0, 0, S, S);
  const im = g.getImageData(0, 0, S, S);
  const d = im.data;
  const solid = (i: number) => d[i * 4 + 3]! >= 128;
  // Paint, not a photograph: lift a dim render until its bright tones read like paint (colourful
  // heroes hardly change, dark-suited ones stop being silhouettes).
  const tones: number[] = [];
  for (let i = 0; i < S * S; i += 5) if (solid(i)) tones.push(0.299 * d[i * 4]! + 0.587 * d[i * 4 + 1]! + 0.114 * d[i * 4 + 2]!);
  tones.sort((a, b) => a - b);
  const lift = Math.min(1.8, Math.max(1, 215 / Math.max(1, tones[Math.floor(tones.length * 0.9)] ?? 215)));
  if (lift > 1.01) for (let o = 0; o < d.length; o += 4) for (let k = 0; k < 3; k++) d[o + k] = d[o + k]! * lift;
  // Compare two pixels apart, so texture filtering's soft seams still read as one boundary.
  const edge = new Uint8Array(S * S);
  for (let y = 0; y < S - 2; y++) {
    for (let x = 0; x < S - 2; x++) {
      const i = y * S + x;
      for (const j of [i + 2, i + 2 * S]) {
        const a = solid(i);
        if (a !== solid(j)) edge[i] = 1;
        else if (a) {
          const o = i * 4;
          const p = j * 4;
          if (Math.abs(d[o]! - d[p]!) + Math.abs(d[o + 1]! - d[p + 1]!) + Math.abs(d[o + 2]! - d[p + 2]!) > 110) edge[i] = 1;
        }
      }
    }
  }
  const [ir, ig, ib] = [1, 3, 5].map((i) => parseInt(ink.slice(i, i + 2), 16)) as [number, number, number];
  const R = Math.max(2, Math.round(S / 180));
  const out = new Uint8ClampedArray(d);
  for (let i = 0; i < S * S; i++) if (d[i * 4 + 3]! >= 128) out[i * 4 + 3] = 255;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      if (!edge[y * S + x]) continue;
      for (let dy = -R; dy <= R; dy++) {
        for (let dx = -R; dx <= R; dx++) {
          const X = x + dx + 1;
          const Y = y + dy + 1;
          if (dx * dx + dy * dy > R * R + 0.5 || X < 0 || Y < 0 || X >= S || Y >= S) continue;
          const o = (Y * S + X) * 4;
          out[o] = ir;
          out[o + 1] = ig;
          out[o + 2] = ib;
          out[o + 3] = 255;
        }
      }
    }
  }
  im.data.set(out);
  g.putImageData(im, 0, 0);
  return cv;
}

/** Shrinks `font(px)` from maxH until `text` fits maxW; returns the size it settled on. */
export function fit(g: G, text: string, font: (px: number) => string, maxW: number, maxH: number) {
  let px = maxH;
  g.font = font(px);
  while (px > 6 && g.measureText(text).width > maxW) {
    px *= 0.94;
    g.font = font(px);
  }
  return px;
}

export function roundRect(g: G, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

/** Mixes "#rrggbb" towards another by k (0..1). */
export function mix(hex: string, to: string, k: number) {
  const a = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const b = [1, 3, 5].map((i) => parseInt(to.slice(i, i + 2), 16));
  return `rgb(${a.map((v, i) => Math.round(v + (b[i]! - v) * k)).join(",")})`;
}
